import {
    differenceInCalendarDays,
    differenceInMilliseconds,
    differenceInMinutes
} from 'date-fns';
import { hasUserConsentedToPersonalizedAds } from '@zoopla/adverts';

import { Event, PageAndDays, PageIdentifier, PageVisit } from './tracker.types';

interface Session {
    pages: {
        [key: string]: PageVisit;
    };
    events: {
        [key: string]: Event;
    };
    startTime: Date;
}

interface SessionHistory {
    [key: string]: Session;
}

class Tracker {
    private trackerType: string;
    private trackerHistory: string;
    private session: null | Session;
    private personalisationConsent: boolean | null;

    constructor(type: string) {
        this.trackerType = type;
        this.trackerHistory = `${type}-history`;
        this.session = null;
        this.personalisationConsent = null;
    }

    public setSession() {
        this.personalisationConsent =
            hasUserConsentedToPersonalizedAds(document);

        if (this.session || !this.personalisationConsent) {
            return;
        }

        window.addEventListener(
            'storage',
            this.subscribeToOtherTabs.bind(this)
        );

        const sessionInStorage = JSON.parse(
            localStorage.getItem(this.trackerType) || '{}'
        );

        // if the record is empty create a new session and set it
        if (!sessionInStorage.startTime) {
            this.session = {
                pages: {},
                events: {},
                startTime: new Date()
            };

            this.setCurrent();
            return;
        }

        const startTime = new Date(sessionInStorage.startTime);

        // if the record was started less than 30 minutes ago, pick it up
        if (differenceInMinutes(new Date(), startTime) < 30) {
            this.session = {
                ...sessionInStorage,
                startTime
            };

            return;
        }

        // if the record was started more than 30 minutes ago, set it in the history and create a new one
        this.setToHistory({ ...sessionInStorage, startTime });

        this.session = {
            pages: {},
            events: {},
            startTime: new Date()
        };

        this.setCurrent();

        this.manageOlderHistory();
    }

    private subscribeToOtherTabs(event: StorageEvent) {
        if (event.key === this.trackerType) {
            this.session = event.newValue
                ? JSON.parse(event.newValue)
                : {
                      pages: {},
                      events: {},
                      startTime: new Date()
                  };
        }
    }

    private setCurrent() {
        if (!this.session) {
            this.setSession();
        }

        // update current localStorage record with the new session data
        localStorage.setItem(
            this.trackerType,
            JSON.stringify({
                ...this.session,
                startTime: this.session?.startTime.toISOString() as string
            })
        );
    }

    private setToHistory(session: Session) {
        const existingSessions = JSON.parse(
            localStorage.getItem(this.trackerHistory) || '{}'
        );
        const key = session.startTime.toISOString();

        localStorage.setItem(
            this.trackerHistory,
            JSON.stringify({
                ...existingSessions,
                [key]: {
                    ...session,
                    startTime: key
                }
            })
        );
    }

    // this method is async so it doesn't block the thread from
    // parsing subsequent code
    private async manageOlderHistory() {
        const historySessions = JSON.parse(
            localStorage.getItem(this.trackerHistory) || '{}'
        );

        const cleanedHistory = Object.keys(historySessions).reduce(
            (acc, currentKey) => {
                if (
                    differenceInCalendarDays(
                        new Date(),
                        new Date(currentKey)
                    ) >= 7
                ) {
                    return acc;
                }

                return {
                    ...acc,
                    [currentKey]: historySessions[currentKey]
                };
            },
            {}
        );

        localStorage.setItem(
            this.trackerHistory,
            JSON.stringify(cleanedHistory)
        );
    }

    private getAllSessions(): SessionHistory {
        //merge the existing session and history
        const historySessions = JSON.parse(
            localStorage.getItem(this.trackerHistory) || '{}'
        );
        return {
            ...(this.session?.startTime && {
                [this.session.startTime.toISOString()]: { ...this.session }
            }),
            ...historySessions
        };
    }

    public track(page: Omit<PageVisit, 'visits'>) {
        if (!this.session) {
            this.setSession();
        }

        if (!this.personalisationConsent) {
            return;
        }

        const existingVisited = this.session?.pages[page.type];

        if (existingVisited) {
            existingVisited.visits += 1;
        } else {
            (this.session as Session).pages[page.type] = {
                ...page,
                visits: 1
            };
        }

        this.setCurrent();
    }

    public getNumberOfPages(identifier: PageIdentifier) {
        if (!this.session || !this.personalisationConsent) {
            return;
        }

        if ('url' in identifier) {
            const page = Object.values(this.session.pages).find(
                ({ url }) => url === identifier.url
            );

            return page?.visits;
        }

        return this.session.pages[identifier.type]?.visits;
    }

    public getNumberOfPagesInDays(page: PageAndDays) {
        const allSessions = this.getAllSessions();

        return Object.keys(allSessions).reduce((acc, currentKey) => {
            // if older than the number of days don't increment
            if (
                differenceInCalendarDays(new Date(), new Date(currentKey)) >
                page.days
            ) {
                return acc;
            }

            const currentSession = allSessions[currentKey];
            if ('url' in page) {
                const pageInSession = Object.values(currentSession.pages).find(
                    ({ url }) => url === page.url
                );

                return acc + (pageInSession?.visits || 0);
            }

            return acc + (currentSession.pages[page.type]?.visits || 0);
        }, 0);
    }

    public getTimeSpent() {
        if (!this.session || !this.personalisationConsent) {
            return;
        }

        const currentTime = new Date();

        return differenceInMilliseconds(currentTime, this.session.startTime);
    }

    public trackEvent(event: string) {
        if (!this.session) {
            this.setSession();
        }

        if (!this.personalisationConsent) {
            return;
        }

        const existingVisited = this.session?.events[event];

        if (existingVisited) {
            existingVisited.count += 1;
        } else {
            (this.session as Session).events[event] = {
                count: 1
            };
        }

        this.setCurrent();
    }

    public getNumberOfEvents(event: string) {
        if (!this.session || !this.personalisationConsent) {
            return;
        }
        return this.session.events[event]?.count;
    }

    public getNumberOfEventsForDays(event: string, days: number): number {
        const allSessions = this.getAllSessions();

        return Object.keys(allSessions).reduce((acc, currentKey) => {
            if (
                differenceInCalendarDays(new Date(), new Date(currentKey)) >
                days
            ) {
                return acc;
            }

            const currentSession = allSessions[currentKey];
            return acc + (currentSession?.events[event]?.count || 0);
        }, 0);
    }

    public destroy() {
        this.session = null;
        window.removeEventListener('storage', this.subscribeToOtherTabs);
    }
}

export const NudgesTracker = new Tracker('nudges-tracker');
